import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';
import OSM from 'ol/source/OSM';
import ImageWMS from 'ol/source/ImageWMS';
import ImageLayer from 'ol/layer/Image';
import LayerGroup from 'ol/layer/Group';
import { fromLonLat } from 'ol/proj';
import XYZ from 'ol/source/XYZ';
import { sksisUrls } from '@/lib/urls';
import { MAP_ZOOM_LEVEL } from '@/lib/constants';

interface LayerWithMetadata {
  layer: TileLayer<TileWMS> | ImageLayer<ImageWMS> | TileLayer<OSM> | TileLayer<XYZ> | LayerGroup;
  name: string;
  value: string;
  desc?: string;
  minVis?: number;
  maxVis?: number;
}

const geoserverBaseURL = new sksisUrls().geoserverBaseURL;
const webMercatorProjection = 'EPSG:3857';

export const mapView = new View({
  center: fromLonLat([-105.75, 52.1334]),
  zoom: MAP_ZOOM_LEVEL,
  resolutions: [
    156543.03392804097 + 32,
    78271.51696402048 + 32,
    39135.75848201024 + 32,
    19567.87924100512 + 32,
    9783.93962050256 + 32,
    4891.96981025128 + 32,
    2445.98490512564 + 32,
    1222.99245256282 + 32,
    611.49622628141 + 32,
    305.748113140705 + 32,
    152.8740565703525 + 32,
    76.43702828517625 + 32,
    38.21851414258813 + 32,
    35.000070000140006,
    14.000028000056009,
    7.000014000028009,
    3.500007000014009,
    1.400002800006,
  ],
});

// OpenLayers Aerial Basemap (using ESRI World Imagery - satellite imagery without annotations)
const aerialBasemap = new TileLayer({
  properties: {
    name: 'Aerial',
  },
  visible: true,
  source: new XYZ({
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    crossOrigin: 'anonymous',
    attributions: '© Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
  }),
});

// OpenLayers Hybrid Basemap (using ESRI World Imagery with boundaries, places, and transportation)
const hybridBasemap = new LayerGroup({
  properties: {
    name: 'Hybrid',
  },
  visible: false,
  layers: [
    new TileLayer({
      source: new XYZ({
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        crossOrigin: 'anonymous',
        attributions: '© Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
      }),
    }),
    new TileLayer({
      source: new XYZ({
        url: 'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
        crossOrigin: 'anonymous',
        attributions: '© Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
      }),
    }),
    new TileLayer({
      source: new XYZ({
        url: 'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}',
        crossOrigin: 'anonymous',
        attributions: '© Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
      }),
    }),
  ],
});

// Open Street Basemap
const openStreeMap = new TileLayer({
  properties: {
    name: 'Roads',
  },
  visible: false,
  source: new OSM(),
});

// Dark Matter Basemap
const darkMatter = new TileLayer({
  properties: {
    name: 'Dark',
  },
  visible: false,
  source: new OSM({
    url: 'http://{a-c}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
  }),
});

export const basemaps = [aerialBasemap, hybridBasemap, openStreeMap, darkMatter];

// SK soil polygons map layer
export const polygonsLayer = new TileLayer({
  visible: true,
  properties: {
    name: 'Polygons',
  },
  source: new TileWMS({
    projection: webMercatorProjection,
    url: geoserverBaseURL,
    params: {
      VERSION: '1.1.1',
      tiled: true,
      STYLES: 'soil_zone_sld',
      LAYERS: 'dss:polygon_theme',
    },
    crossOrigin: 'anonymous',
  }),
});

export const labelsLayer = new TileLayer({
  visible: false,
  properties: {
    name: 'Polygon Labels',
  },
  source: new TileWMS({
    url: geoserverBaseURL,
    params: {
      FORMAT: 'image/png',
      VERSION: '1.1.1',
      tiled: true,
      STYLES: '',
      LAYERS: 'dss:polygon_label_layer',
    },
    crossOrigin: 'anonymous',
  }),
});

export const sectionLayer = new ImageLayer({
  visible: true,
  properties: {
    name: 'Sections',
  },
  source: new ImageWMS({
    ratio: 1,
    url: geoserverBaseURL,
    params: {
      FORMAT: 'image/png',
      VERSION: '1.1.1',
      STYLES: '',
      LAYERS: 'dss:section',
    },
    crossOrigin: 'anonymous',
  }),
});

export const treatyLayer = new ImageLayer({
  visible: false,
  properties: {
    name: 'Treaty Territories',
  },
  source: new ImageWMS({
    ratio: 1,
    url: geoserverBaseURL,
    params: {
      FORMAT: 'image/png',
      VERSION: '1.1.1',
      STYLES: '',
      LAYERS: 'dss:treaty',
    },
    crossOrigin: 'anonymous',
  }),
});

export const ruralMunicipalities = new ImageLayer({
  visible: true,
  properties: {
    name: 'Rural Municipalities',
  },
  source: new ImageWMS({
    ratio: 1,
    url: geoserverBaseURL,
    params: {
      FORMAT: 'image/png',
      VERSION: '1.1.1',
      STYLES: '',
      LAYERS: 'dss:rm',
    },
    crossOrigin: 'anonymous',
  }),
});

export const township = new ImageLayer({
  visible: true,
  properties: {
    name: 'Townships',
  },
  source: new ImageWMS({
    ratio: 1,
    url: geoserverBaseURL,
    params: {
      FORMAT: 'image/png',
      VERSION: '1.1.1',
      STYLES: '',
      LAYERS: 'dss:township',
    },
    crossOrigin: 'anonymous',
  }),
});

export const urbanCenters = new ImageLayer({
  visible: true,
  properties: {
    name: 'Urban Centres',
  },
  source: new ImageWMS({
    ratio: 1,
    url: geoserverBaseURL,
    params: {
      FORMAT: 'image/png',
      VERSION: '1.1.1',
      STYLES: '',
      LAYERS: 'dss:sk_urban',
    },
    crossOrigin: 'anonymous',
  }),
});

export const indianReserve = new ImageLayer({
  visible: false,
  properties: {
    name: 'FN Reserves',
  },
  source: new ImageWMS({
    ratio: 1,
    url: geoserverBaseURL,
    params: {
      FORMAT: 'image/png',
      VERSION: '1.1.1',
      STYLES: '',
      LAYERS: 'dss:saskadmin_2016_indian_reserve.shp',
    },
    crossOrigin: 'anonymous',
  }),
});

export const watersheds = new ImageLayer({
  visible: false,
  properties: {
    name: 'Watersheds',
  },
  source: new ImageWMS({
    ratio: 1,
    url: geoserverBaseURL,
    params: {
      FORMAT: 'image/png',
      VERSION: '1.1.1',
      STYLES: '',
      LAYERS: 'dss:watersheds',
    },
    crossOrigin: 'anonymous',
  }),
});

export const irrigationDistrict = new ImageLayer({
  visible: false,
  properties: {
    name: 'Irrigation Districts',
  },
  source: new ImageWMS({
    ratio: 1,
    url: geoserverBaseURL,
    params: {
      FORMAT: 'image/png',
      VERSION: '1.1.1',
      STYLES: '',
      LAYERS: 'dss:sk_irrigation_district',
    },
    crossOrigin: 'anonymous',
  }),
});

export const layers: LayerWithMetadata[] = [
  {
    layer: polygonsLayer,
    name: 'Polygons',
    desc:
      'Soil polygon information is based on previous collected  Soil Survey  information at scale of 1:100,000. ' +
      'Labels are displayed as red when zoomed in past the appropriate (intended) scale.',
    minVis: 1,
    maxVis: 20,
    value: 'polygonsLayer',
  },
  {
    layer: labelsLayer,
    name: 'Polygon Labels',
    desc: 'Polygon labels and outlines',
    minVis: 14,
    maxVis: 20,
    value: 'labelsLayer',
  },
  {
    layer: township,
    name: 'Townships',
    desc: 'Visible only to a scale of 1:50,000',
    minVis: 1,
    maxVis: 20,
    value: 'township',
  },
  {
    layer: ruralMunicipalities,
    name: 'Rural Municipalities',
    desc: 'Visible only to a scale of 1:125,000',
    minVis: 6,
    maxVis: 15,
    value: 'ruralMunicipalities',
  },
  {
    layer: sectionLayer,
    name: 'Sections',
    desc: 'Section lines when on will be visible at a scale of 1:250,000 and greater.',
    minVis: 1,
    maxVis: 20,
    value: 'sectionLayer',
  },
  {
    layer: urbanCenters,
    name: 'Urban Centres',
    desc: 'Saskatchewan cities and towns',
    minVis: 1,
    maxVis: 20,
    value: 'urbanCenters',
  },
  {
    layer: treatyLayer,
    name: 'Treaty Territories',
    desc:
      'Pre-1975 treaty territory  boundaries. Contains information licensed under the Open Government ' +
      'Licence – Canada. https://open.canada.ca/en/',
    minVis: 1,
    maxVis: 20,
    value: 'treatyLayer',
  },
  {
    layer: indianReserve,
    name: 'FN Reserves',
    desc: 'First Nation reserve land circa 2015',
    minVis: 1,
    maxVis: 20,
    value: 'indianReserve',
  },
  {
    layer: watersheds,
    name: 'Watersheds',
    minVis: 1,
    maxVis: 20,
    value: 'watersheds',
  },
  {
    layer: irrigationDistrict,
    name: 'Irrigation Districts',
    desc: 'Saskatchewan irrigation districts, supplied by Crops and Irrigation, Sask Ministry of Ag',
    minVis: 1,
    maxVis: 20,
    value: 'irrigationDistrict',
  },
];
